import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Search, Package, ArrowUpDown, TrendingUp, TrendingDown, AlertCircle,
  BarChart3, Layers, DollarSign, Clock, ShoppingCart,
  ChevronLeft, ChevronRight, Calendar, Lock, Download,
  HeartPulse, CheckCircle2, XCircle, AlertTriangle, Users, RefreshCw, Save, ExternalLink, CalendarClock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import * as XLSX from 'xlsx';

const API = '/api';

function formatCurrency(val) {
  const num = parseFloat(val);
  if (isNaN(num)) return val || '';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' Kč';
}

function formatDate(str) {
  if (!str) return '';
  try {
    const d = new Date(str);
    return isNaN(d.getTime()) ? str : d.toLocaleDateString('cs-CZ');
  } catch { return str; }
}

// ==================== L1 DASHBOARD SECTION ====================
function L1DashboardSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: resp } = await axios.get(`${API}/l1dashboard`, { withCredentials: true });
        setData(resp);
      } catch { /* no data */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="p-6 text-center text-[#57534E]">Načítám L1 Dashboard...</div>;
  if (!data || !data.weeks?.length) return null;

  const pctColumns = new Set(['pct_fp', 'nedod_pct_cena', 'nedod_pct_mn', 'pct_marze', 'pct_rozdil',
    'pct_porce', 'pct_zak', 'pct_adres', 'pct_trzby',
    'pomer_24_25', 'pomer_25_25', 'pomer_26_25', 'dod_pomer_24', 'dod_pomer_25', 'dod_pomer_26',
    'marze_pct', 'marze_zs', 'marze_nav', 'marze_kons']);

  const isPctColumn = (colKey) => {
    if (pctColumns.has(colKey)) return true;
    if (colKey.startsWith('pct_') || colKey.startsWith('pomer_') || colKey.endsWith('_pct')) return true;
    if (colKey.includes('marze') && !colKey.includes('czk')) return true;
    return false;
  };

  const fmtNum = (v, colKey) => {
    if (v == null || v === '') return '—';
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return String(v);
    if (isPctColumn(colKey)) return (n * 100).toFixed(1) + ' %';
    return n.toLocaleString('cs-CZ', { maximumFractionDigits: 2 });
  };

  const sectionKeys = Object.keys(data.sections);

  const sectionColors = {
    nakup: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: ShoppingCart },
    stav_skladu: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: Package },
    sklad_neshod: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertCircle },
    vydej_spotreba: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', icon: Layers },
    hotovky_dodano: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', icon: TrendingUp },
    hotovky_prodej: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', icon: DollarSign },
    porovnani_cen: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', icon: BarChart3 },
    call_centrum: { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700', icon: Clock },
  };

  return (
    <div className="space-y-4" data-testid="l1-dashboard-section">
      <div className="rounded-2xl bg-gradient-to-br from-emerald-50/80 to-teal-50/40 border border-emerald-100 p-6 space-y-4">
        <h2 className="text-xl font-bold text-[#052E16] font-['Manrope']">L1 Dashboard</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {sectionKeys.map((key) => {
          const meta = data.sections[key];
          const colors = sectionColors[key] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: BarChart3 };
          const Icon = colors.icon;
          const isOpen = openSection === key;
          return (
            <button
              key={key}
              onClick={() => setOpenSection(isOpen ? null : key)}
              data-testid={`l1-section-${key}`}
              className={`${colors.bg} ${colors.border} border rounded-xl p-4 text-left transition-all hover:shadow-md ${isOpen ? 'ring-2 ring-offset-1 ring-[#16A34A]' : ''}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${colors.text}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${colors.text}`}>{meta.label}</span>
              </div>
              <p className="text-[10px] text-[#94A3B8]">{data.weeks.length} týdnů</p>
            </button>
          );
        })}
      </div>

      {openSection && data.sections[openSection] && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope']">
              {data.sections[openSection].label}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0]">
                    <TableHead className="text-[#57534E] sticky left-0 bg-white z-10 min-w-[60px]">Týden</TableHead>
                    {data.sections[openSection].cols.map(([colKey, colLabel]) => (
                      <TableHead key={colKey} className="text-[#57534E] text-right min-w-[100px] whitespace-nowrap text-xs">
                        {colLabel}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.celorok?.[openSection] && (
                    <TableRow className="bg-[#F0FDF4] font-semibold border-[#BBF7D0]">
                      <TableCell className="sticky left-0 bg-[#F0FDF4] z-10 text-xs font-bold text-[#16A34A]">Celorok</TableCell>
                      {data.sections[openSection].cols.map(([colKey]) => (
                        <TableCell key={colKey} className="text-right text-xs text-[#052E16]">
                          {fmtNum(data.celorok[openSection][colKey], colKey)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                  {data.weeks.map((week) => (
                    <TableRow key={week.week} className="border-[#E2E8F0] hover:bg-[#F8FAF8]">
                      <TableCell className="sticky left-0 bg-white z-10 text-xs font-semibold text-[#052E16]">{week.week}</TableCell>
                      {data.sections[openSection].cols.map(([colKey]) => {
                        const val = week[openSection]?.[colKey];
                        return (
                          <TableCell key={colKey} className="text-right text-xs text-[#57534E] tabular-nums">
                            {fmtNum(val, colKey)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Extra sheets */}
      {data.extra_sheets?.length > 0 && data.extra_sheets.map((sheet, sheetIdx) => {
        const sheetColors = [
          { bg: 'bg-gradient-to-br from-indigo-50/60 to-blue-50/40', border: 'border-indigo-100', cardBg: 'bg-indigo-50', cardBorder: 'border-indigo-200', cardText: 'text-indigo-700' },
          { bg: 'bg-gradient-to-br from-pink-50/60 to-fuchsia-50/40', border: 'border-pink-100', cardBg: 'bg-pink-50', cardBorder: 'border-pink-200', cardText: 'text-pink-700' },
          { bg: 'bg-gradient-to-br from-cyan-50/60 to-sky-50/40', border: 'border-cyan-100', cardBg: 'bg-cyan-50', cardBorder: 'border-cyan-200', cardText: 'text-cyan-700' },
          { bg: 'bg-gradient-to-br from-lime-50/60 to-green-50/40', border: 'border-lime-100', cardBg: 'bg-lime-50', cardBorder: 'border-lime-200', cardText: 'text-lime-700' },
          { bg: 'bg-gradient-to-br from-orange-50/60 to-amber-50/40', border: 'border-orange-100', cardBg: 'bg-orange-50', cardBorder: 'border-orange-200', cardText: 'text-orange-700' },
          { bg: 'bg-gradient-to-br from-violet-50/60 to-purple-50/40', border: 'border-violet-100', cardBg: 'bg-violet-50', cardBorder: 'border-violet-200', cardText: 'text-violet-700' },
        ];
        const c = sheetColors[sheetIdx % sheetColors.length];
        return (
        <div key={sheet.key} className={`rounded-2xl ${c.bg} ${c.border} border p-6 space-y-4`}>
          <h3 className="text-base font-bold text-[#052E16] font-['Manrope']">{sheet.label}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(sheet.sections).map(([secKey, sec]) => {
              const fullKey = `${sheet.key}_${secKey}`;
              const isOpen = openSection === fullKey;
              return (
                <button
                  key={fullKey}
                  onClick={async () => {
                    if (isOpen) { setOpenSection(null); setSheetData(null); return; }
                    setOpenSection(fullKey);
                    setSheetLoading(true);
                    try {
                      const { data: sd } = await axios.get(`${API}/l1dashboard/sheet/${sheet.key}/${secKey}`, { withCredentials: true });
                      setSheetData(sd);
                    } catch { setSheetData(null); }
                    finally { setSheetLoading(false); }
                  }}
                  className={`${c.cardBg} ${c.cardBorder} border rounded-xl p-4 text-left transition-all hover:shadow-md ${isOpen ? 'ring-2 ring-offset-1 ring-[#16A34A]' : ''}`}
                >
                  <span className={`text-xs font-bold uppercase tracking-wider ${c.cardText}`}>{sec.label}</span>
                  <p className="text-[10px] text-[#94A3B8]">{sec.row_count} {secKey === 'detail' ? 'záznamů' : 'týdnů'}</p>
                </button>
              );
            })}
          </div>
          {/* Expanded section table */}
          {Object.keys(sheet.sections).some(sk => openSection === `${sheet.key}_${sk}`) && sheetData && !sheetLoading && (
            <Card className="bg-white border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope']">
                  {sheetData.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  {(() => {
                    const hasName = sheetData.rows?.some(r => r.name);
                    return (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E2E8F0]">
                        <TableHead className="text-[#57534E] sticky left-0 bg-white z-10 min-w-[60px]">Týden</TableHead>
                        {hasName && (
                          <TableHead className="text-[#57534E] min-w-[200px] text-xs">Název / OM</TableHead>
                        )}
                        {sheetData.cols?.map(([colKey, colLabel]) => (
                          <TableHead key={colKey} className="text-[#57534E] text-right min-w-[90px] whitespace-nowrap text-xs">
                            {colLabel}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sheetData.rows?.map((row, idx) => (
                        <TableRow key={idx} className="border-[#E2E8F0] hover:bg-[#F8FAF8]">
                          <TableCell className="sticky left-0 bg-white z-10 text-xs font-semibold text-[#052E16]">{row.week}</TableCell>
                          {hasName && (
                            <TableCell className="text-xs text-[#1C1917]">{row.name || '—'}</TableCell>
                          )}
                          {sheetData.cols?.map(([colKey]) => {
                            const val = row[colKey];
                            return (
                              <TableCell key={colKey} className="text-right text-xs text-[#57534E] tabular-nums">
                                {fmtNum(val, colKey)}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}
          {Object.keys(sheet.sections).some(sk => openSection === `${sheet.key}_${sk}`) && sheetLoading && (
            <div className="p-4 text-center text-[#57534E] text-sm">Načítám...</div>
          )}
        </div>
        );
      })}
      </div>
    </div>
  );
}

// ==================== DASHBOARD TAB ====================
function OverviewDashboard() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllIncreases, setShowAllIncreases] = useState(false);
  const [showAllDecreases, setShowAllDecreases] = useState(false);
  const [openCard, setOpenCard] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, trendsRes] = await Promise.all([
          axios.get(`${API}/warehouse/stats`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/purchases/trends`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setTrends(trendsRes.data);
      } catch (e) {
        console.error('Dashboard fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return <div className="p-12 text-center text-[#57534E]">Načítám statistiky...</div>;
  }

  const hasPurchases = trends && trends.total_items > 0;
  const hasWarehouse = stats && stats.total > 0;

  if (!hasPurchases && !hasWarehouse) {
    return (
      <div className="space-y-10">
        <L1DashboardSection />
        <div className="p-12 text-center text-[#57534E]">
          <Package className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
          <p className="font-medium">Žádná data k vyhodnocení</p>
          <p className="text-sm mt-1">Importujte Excel v administraci.</p>
        </div>
      </div>
    );
  }

  const cards = [];
  if (hasPurchases) {
    cards.push(
      { key: 'increases', label: 'Největší zdražení', desc: `T${trends.prev_week} → T${trends.ref_week}`, icon: TrendingUp, bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', count: trends.biggest_increases.length },
      { key: 'decreases', label: 'Největší zlevnění', desc: `T${trends.prev_week} → T${trends.ref_week}`, icon: TrendingDown, bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', count: trends.biggest_decreases.length },
      { key: 'rising', label: 'Stoupající trend', desc: '2+ po sobě jdoucí týdny', icon: TrendingUp, bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', count: trends.rising_trend.length },
      { key: 'falling', label: 'Klesající trend', desc: '2+ po sobě jdoucí týdny', icon: TrendingDown, bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', count: trends.falling_trend.length },
    );
  }
  if (hasWarehouse) {
    cards.push(
      { key: 'wh-summary', label: 'Sklad NES – Souhrn', desc: `${stats.total} položek`, icon: Package, bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700' },
      { key: 'wh-umisteni', label: 'Podle umístění', desc: `${stats.by_umisteni.length} kategorií`, icon: BarChart3, bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
      { key: 'wh-dispozice', label: 'Největší dispozice', desc: 'Top položky', icon: Layers, bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', count: stats.top_dispozice?.length },
      { key: 'wh-expiring', label: 'Blížící se expirace', desc: '90 dní', icon: AlertCircle, bg: stats.expiring_soon.length > 0 ? 'bg-red-50' : 'bg-gray-50', border: stats.expiring_soon.length > 0 ? 'border-red-200' : 'border-gray-200', text: stats.expiring_soon.length > 0 ? 'text-red-700' : 'text-gray-500', count: stats.expiring_soon.length },
    );
  }

  return (
    <div className="space-y-6">
      {/* ===== L1 DASHBOARD ===== */}
      <L1DashboardSection />

      {/* ===== CARDS GRID ===== */}
      {hasPurchases && (
        <div className="rounded-2xl bg-gradient-to-br from-rose-50/60 to-orange-50/40 border border-rose-100 p-6 space-y-4">
          <h2 className="text-xl font-bold text-[#052E16] font-['Manrope']">Položky nákupu</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.filter(c => ['increases','decreases','rising','falling'].includes(c.key)).map((card) => {
              const Icon = card.icon;
              const isOpen = openCard === card.key;
              return (
                <button key={card.key} onClick={() => setOpenCard(isOpen ? null : card.key)}
                  data-testid={`dash-card-${card.key}`}
                  className={`${card.bg} ${card.border} border rounded-xl p-4 text-left transition-all hover:shadow-md ${isOpen ? 'ring-2 ring-offset-1 ring-[#16A34A]' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${card.text}`} />
                    <span className={`text-xs font-bold uppercase tracking-wider ${card.text}`}>{card.label}</span>
                  </div>
                  <p className="text-[10px] text-[#94A3B8]">{card.desc}{card.count != null ? ` · ${card.count} položek` : ''}</p>
                </button>
              );
            })}
          </div>

          {/* Expanded purchase content */}
          {openCard === 'increases' && trends.biggest_increases.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="biggest-increases">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-500" /> Největší zdražení
              <span className="text-[10px] font-normal text-[#94A3B8]">T{trends.prev_week} → T{trends.ref_week}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <TableHead className="text-[#57534E]">Surovina</TableHead>
                    <TableHead className="text-[#57534E] text-right">Týden</TableHead>
                    <TableHead className="text-[#57534E] text-right">Cena z</TableHead>
                    <TableHead className="text-[#57534E] text-right">Cena na</TableHead>
                    <TableHead className="text-[#57534E] text-right">Změna</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(showAllIncreases ? trends.biggest_increases : trends.biggest_increases.slice(0, 10)).map((item, idx) => (
                    <TableRow key={idx} className="border-[#E2E8F0] hover:bg-red-50/50">
                      <TableCell className="max-w-[180px]">
                        <span className="text-sm text-[#1C1917] line-clamp-1" title={item.nazev}>{item.nazev}</span>
                        <span className="text-[10px] text-[#94A3B8] block">{item.zbozi_id}</span>
                      </TableCell>
                      <TableCell className="text-right text-xs text-[#57534E] whitespace-nowrap">{item.from_week}→{item.to_week}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-[#57534E]">{item.from_price.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-[#1C1917] font-medium">{item.to_price.toFixed(1)}</TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-0.5 text-xs font-bold text-red-600">
                          <TrendingUp className="w-3 h-3" />+{Math.abs(item.diff_pct) < 1 ? item.diff_pct.toFixed(2) : item.diff_pct.toFixed(1)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {trends.biggest_increases.length > 10 && (
              <div className="p-2 border-t border-[#E2E8F0] text-center">
                <Button variant="ghost" size="sm" onClick={() => setShowAllIncreases(!showAllIncreases)} data-testid="toggle-all-increases" className="text-xs text-[#16A34A] hover:text-[#15803D]">
                  {showAllIncreases ? 'Zobrazit méně' : `Zobrazit vše (${trends.biggest_increases.length})`}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {openCard === 'decreases' && trends.biggest_decreases.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="biggest-decreases">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-green-600" /> Největší zlevnění
              <span className="text-[10px] font-normal text-[#94A3B8]">T{trends.prev_week} → T{trends.ref_week}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <TableHead className="text-[#57534E]">Surovina</TableHead>
                    <TableHead className="text-[#57534E] text-right">Týden</TableHead>
                    <TableHead className="text-[#57534E] text-right">Cena z</TableHead>
                    <TableHead className="text-[#57534E] text-right">Cena na</TableHead>
                    <TableHead className="text-[#57534E] text-right">Změna</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(showAllDecreases ? trends.biggest_decreases : trends.biggest_decreases.slice(0, 10)).map((item, idx) => (
                    <TableRow key={idx} className="border-[#E2E8F0] hover:bg-green-50/50">
                      <TableCell className="max-w-[180px]">
                        <span className="text-sm text-[#1C1917] line-clamp-1" title={item.nazev}>{item.nazev}</span>
                        <span className="text-[10px] text-[#94A3B8] block">{item.zbozi_id}</span>
                      </TableCell>
                      <TableCell className="text-right text-xs text-[#57534E] whitespace-nowrap">{item.from_week}→{item.to_week}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-[#57534E]">{item.from_price.toFixed(1)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-[#1C1917] font-medium">{item.to_price.toFixed(1)}</TableCell>
                      <TableCell className="text-right">
                        <span className="inline-flex items-center gap-0.5 text-xs font-bold text-green-600">
                          <TrendingDown className="w-3 h-3" />{Math.abs(item.diff_pct) < 1 ? item.diff_pct.toFixed(2) : item.diff_pct.toFixed(1)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {trends.biggest_decreases.length > 10 && (
              <div className="p-2 border-t border-[#E2E8F0] text-center">
                <Button variant="ghost" size="sm" onClick={() => setShowAllDecreases(!showAllDecreases)} data-testid="toggle-all-decreases" className="text-xs text-[#16A34A] hover:text-[#15803D]">
                  {showAllDecreases ? 'Zobrazit méně' : `Zobrazit vše (${trends.biggest_decreases.length})`}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {openCard === 'rising' && trends.rising_trend.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="rising-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-red-400" /> Stoupající trend ceny
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <TableHead className="text-[#57534E]">Surovina</TableHead>
                    <TableHead className="text-[#57534E] text-right">Období</TableHead>
                    <TableHead className="text-[#57534E] text-right">Z → Na</TableHead>
                    <TableHead className="text-[#57534E] text-right">Celkem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trends.rising_trend.map((item, idx) => (
                    <TableRow key={idx} className="border-[#E2E8F0] hover:bg-red-50/30">
                      <TableCell className="max-w-[200px]"><span className="text-sm text-[#1C1917] line-clamp-1" title={item.nazev}>{item.nazev}</span></TableCell>
                      <TableCell className="text-right text-xs text-[#57534E] whitespace-nowrap">T{item.from_week}–T{item.to_week} ({item.streak}t)</TableCell>
                      <TableCell className="text-right font-mono text-xs text-[#57534E]">{item.first_price.toFixed(1)} → {item.last_price.toFixed(1)}</TableCell>
                      <TableCell className="text-right"><span className="text-xs font-bold text-red-600">+{item.total_change_pct}%</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {openCard === 'falling' && trends.falling_trend.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="falling-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-green-500" /> Klesající trend ceny
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <TableHead className="text-[#57534E]">Surovina</TableHead>
                    <TableHead className="text-[#57534E] text-right">Období</TableHead>
                    <TableHead className="text-[#57534E] text-right">Z → Na</TableHead>
                    <TableHead className="text-[#57534E] text-right">Celkem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trends.falling_trend.map((item, idx) => (
                    <TableRow key={idx} className="border-[#E2E8F0] hover:bg-green-50/30">
                      <TableCell className="max-w-[200px]"><span className="text-sm text-[#1C1917] line-clamp-1" title={item.nazev}>{item.nazev}</span></TableCell>
                      <TableCell className="text-right text-xs text-[#57534E] whitespace-nowrap">T{item.from_week}–T{item.to_week} ({item.streak}t)</TableCell>
                      <TableCell className="text-right font-mono text-xs text-[#57534E]">{item.first_price.toFixed(1)} → {item.last_price.toFixed(1)}</TableCell>
                      <TableCell className="text-right"><span className="text-xs font-bold text-green-600">{item.total_change_pct}%</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
        </div>
      )}

      {hasWarehouse && (
        <div className="rounded-2xl bg-gradient-to-br from-amber-50/60 to-yellow-50/40 border border-amber-100 p-6 space-y-4">
          <h2 className="text-xl font-bold text-[#052E16] font-['Manrope']">Sklad NES</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.filter(c => c.key.startsWith('wh-')).map((card) => {
              const Icon = card.icon;
              const isOpen = openCard === card.key;
              return (
                <button key={card.key} onClick={() => setOpenCard(isOpen ? null : card.key)}
                  data-testid={`dash-card-${card.key}`}
                  className={`${card.bg} ${card.border} border rounded-xl p-4 text-left transition-all hover:shadow-md ${isOpen ? 'ring-2 ring-offset-1 ring-[#16A34A]' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={`w-4 h-4 ${card.text}`} />
                    <span className={`text-xs font-bold uppercase tracking-wider ${card.text}`}>{card.label}</span>
                  </div>
                  <p className="text-[10px] text-[#94A3B8]">{card.desc}{card.count != null ? ` · ${card.count}` : ''}</p>
                </button>
              );
            })}
          </div>

          {/* Expanded warehouse content */}
      {openCard === 'wh-summary' && hasWarehouse && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="stat-total-items">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-[#57534E] uppercase tracking-wider">Celkem položek</p>
                  <p className="text-3xl font-bold text-[#052E16] mt-1">{stats.total.toLocaleString('cs-CZ')}</p>
                </div>
                <div className="p-2.5 bg-[#F0FDF4] rounded-xl"><Layers className="w-5 h-5 text-[#16A34A]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="stat-total-value">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-[#57534E] uppercase tracking-wider">Celková účetní hodnota</p>
                  <p className="text-3xl font-bold text-[#052E16] mt-1">{formatCurrency(stats.total_value)}</p>
                </div>
                <div className="p-2.5 bg-[#FEF3C7] rounded-xl"><DollarSign className="w-5 h-5 text-[#D97706]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="stat-expiring">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-[#57534E] uppercase tracking-wider">Blížící se expirace (90 dní)</p>
                  <p className="text-3xl font-bold text-[#052E16] mt-1">{stats.expiring_soon.length}</p>
                </div>
                <div className={`p-2.5 rounded-xl ${stats.expiring_soon.length > 0 ? 'bg-red-50' : 'bg-[#F0FDF4]'}`}>
                  <AlertCircle className={`w-5 h-5 ${stats.expiring_soon.length > 0 ? 'text-red-500' : 'text-[#16A34A]'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {openCard === 'wh-umisteni' && hasWarehouse && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="chart-umisteni">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#16A34A]" /> Podle umístění
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(() => {
                const umisteniColors = {
                  'VÝPRODEJ': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', bar: 'bg-amber-400' },
                  'SARZE': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', bar: 'bg-blue-400' },
                };
                const defaultColor = { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', bar: 'bg-gray-400' };
                const maxCount = Math.max(...stats.by_umisteni.map(u => u.count), 1);
                return stats.by_umisteni.map((item) => {
                  const c = umisteniColors[item.name] || defaultColor;
                  const pct = Math.round((item.count / maxCount) * 100);
                  return (
                    <div key={item.name}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium px-2 py-0.5 rounded-md ${c.bg} ${c.border} border ${c.text}`}>{item.name}</span>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-[#57534E]">{item.count} záznamů</span>
                          <span className="text-[#1C1917] font-medium">{formatCurrency(item.value)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {openCard === 'wh-dispozice' && hasWarehouse && stats.top_dispozice?.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="chart-top-dispozice">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#16A34A]" /> Největší dispozice
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <TableHead className="text-[#57534E]">Zkratka</TableHead>
                    <TableHead className="text-[#57534E]">Zboží</TableHead>
                    <TableHead className="text-[#57534E] text-right">Dispozice</TableHead>
                    <TableHead className="text-[#57534E] text-right">Účetní hodn.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.top_dispozice.map((item, idx) => (
                    <TableRow key={idx} className="border-[#E2E8F0] hover:bg-[#F0FDF4]">
                      <TableCell className="text-[#1C1917] font-mono text-sm">{item.zkratka}</TableCell>
                      <TableCell className="max-w-[200px]"><span className="line-clamp-1 text-sm">{item.zbozi}</span></TableCell>
                      <TableCell className="text-right font-bold text-[#052E16] text-sm">{Number(item.dispozice).toLocaleString('cs-CZ')}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(item.ucetni_hodnota)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {openCard === 'wh-expiring' && hasWarehouse && stats.expiring_soon.length > 0 && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm" data-testid="table-expiring">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Clock className="w-4 h-4 text-red-500" /> Položky s blížící se expirací
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <TableHead className="text-[#57534E]">Zkratka</TableHead>
                    <TableHead className="text-[#57534E]">Zboží</TableHead>
                    <TableHead className="text-[#57534E]">Expirace</TableHead>
                    <TableHead className="text-[#57534E]">Zbývá</TableHead>
                    <TableHead className="text-[#57534E]">Umístění</TableHead>
                    <TableHead className="text-[#57534E] text-right">Účetní hodn.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.expiring_soon.map((item) => (
                    <TableRow key={item.id} className="border-[#E2E8F0] hover:bg-[#F0FDF4]">
                      <TableCell className="text-[#1C1917] font-mono text-sm">{item.zkratka}</TableCell>
                      <TableCell className="max-w-xs"><span className="line-clamp-1">{item.zbozi}</span></TableCell>
                      <TableCell className="text-[#57534E] whitespace-nowrap">{formatDate(item.expirace)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          item.days_left < 0 ? 'bg-red-100 text-red-800 border border-red-200'
                          : item.days_left <= 30 ? 'bg-orange-100 text-orange-800 border border-orange-200'
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        }`}>
                          {item.days_left < 0 ? `${Math.abs(item.days_left)} dní po` : `${item.days_left} dní`}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.umisteni === 'VÝPRODEJ' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">{item.umisteni}</span>
                        ) : item.umisteni === 'SARZE' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD]">{item.umisteni}</span>
                        ) : (
                          <span className="text-[#57534E]">{item.umisteni}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(item.ucetni_hodnota)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
        </div>
      )}
    </div>
  );
}

// ==================== SKLAD NES TAB ====================
function SkladNesTable() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = debouncedSearch ? { search: debouncedSearch } : {};
        const { data } = await axios.get(`${API}/warehouse`, { params, withCredentials: true });
        setItems(data);
      } catch (error) {
        console.error('Error fetching warehouse data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debouncedSearch]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const numericFields = new Set(['dispozice', 'ucetni_hodnota']);

  const parseNum = (v) => parseFloat(String(v ?? '0').replace(/\s/g, '').replace(',', '.')) || 0;

  // Count items per location (across full result set) – used for filter chips
  const locationCounts = items.reduce((acc, it) => {
    const loc = (it.umisteni || '').trim() || '(prázdné)';
    acc[loc] = (acc[loc] || 0) + 1;
    return acc;
  }, {});
  const locationOptions = Object.keys(locationCounts).sort((a, b) => locationCounts[b] - locationCounts[a]);

  // Apply location filter
  const filteredItems = items.filter(it => {
    if (locationFilter === 'all') return true;
    const loc = (it.umisteni || '').trim() || '(prázdné)';
    return loc === locationFilter;
  });

  const totals = filteredItems.reduce((acc, it) => {
    acc.qty += parseNum(it.dispozice);
    acc.value += parseNum(it.ucetni_hodnota);
    return acc;
  }, { qty: 0, value: 0 });

  const sortedItems = [...filteredItems].sort((a, b) => {
    if (!sortField) return 0;
    if (numericFields.has(sortField)) {
      const numA = parseFloat(String(a[sortField] || '0').replace(/\s/g, '').replace(',', '.')) || 0;
      const numB = parseFloat(String(b[sortField] || '0').replace(/\s/g, '').replace(',', '.')) || 0;
      return sortDir === 'asc' ? numA - numB : numB - numA;
    }
    const valA = (a[sortField] || '').toLowerCase();
    const valB = (b[sortField] || '').toLowerCase();
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const columns = [
    { key: 'zkratka', label: 'Zkratka' },
    { key: 'zbozi', label: 'Zboží' },
    { key: 'expirace', label: 'Expirace' },
    { key: 'umisteni', label: 'Umístění' },
    { key: 'dispozice', label: 'Dispozice' },
    { key: 'ucetni_hodnota', label: 'Účetní hodn.' },
  ];

  const handleExport = () => {
    const rows = sortedItems.map(it => ({
      'Zkratka': it.zkratka || '',
      'Zboží': it.zbozi || '',
      'Expirace': it.expirace || '',
      'Umístění': it.umisteni || '',
      'Dispozice': parseNum(it.dispozice),
      'Účetní hodnota': parseNum(it.ucetni_hodnota),
    }));
    // Append totals row
    rows.push({});
    rows.push({
      'Zkratka': 'CELKEM',
      'Zboží': locationFilter !== 'all' ? `Filtr: ${locationFilter}` : '',
      'Dispozice': totals.qty,
      'Účetní hodnota': totals.value,
    });

    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ['Zkratka', 'Zboží', 'Expirace', 'Umístění', 'Dispozice', 'Účetní hodnota'],
    });
    // Column widths
    ws['!cols'] = [
      { wch: 12 }, { wch: 50 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 16 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sklad NES');

    const today = new Date().toISOString().slice(0, 10);
    const filterTag = locationFilter !== 'all' ? `_${locationFilter}` : '';
    XLSX.writeFile(wb, `sklad_nes${filterTag}_${today}.xlsx`);
  };

  return (
    <div className="space-y-4">
      {/* Location filter chips */}
      {!loading && items.length > 0 && locationOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="location-filter-chips">
          <span className="text-xs font-semibold uppercase text-[#94A3B8]">Umístění:</span>
          <button
            onClick={() => setLocationFilter('all')}
            data-testid="loc-chip-all"
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
              locationFilter === 'all'
                ? 'bg-[#052E16] text-white border-[#052E16]'
                : 'bg-white text-[#57534E] border-[#E2E8F0] hover:border-[#16A34A]'
            }`}
          >
            Vše <span className="opacity-70 ml-1">({items.length})</span>
          </button>
          {locationOptions.map(loc => {
            const active = locationFilter === loc;
            const isVyprodej = loc === 'VÝPRODEJ';
            const isSarze = loc === 'SARZE';
            return (
              <button
                key={loc}
                onClick={() => setLocationFilter(loc)}
                data-testid={`loc-chip-${loc}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? isVyprodej
                      ? 'bg-[#F59E0B] text-white border-[#F59E0B]'
                      : isSarze
                        ? 'bg-[#2563EB] text-white border-[#2563EB]'
                        : 'bg-[#16A34A] text-white border-[#16A34A]'
                    : isVyprodej
                      ? 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A] hover:bg-[#FDE68A]'
                      : isSarze
                        ? 'bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD] hover:bg-[#BFDBFE]'
                        : 'bg-white text-[#57534E] border-[#E2E8F0] hover:border-[#16A34A]'
                }`}
              >
                {loc} <span className="opacity-70 ml-1">({locationCounts[loc]})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Summary tiles */}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="warehouse-summary-tiles">
          <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-[#F0FDF4] to-white shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase font-bold text-[#16A34A]/80">
              <Package className="w-3.5 h-3.5" /> Součet kusů (dispozice)
            </div>
            <div className="text-2xl font-bold text-[#052E16] mt-1 tabular-nums" data-testid="warehouse-total-qty">
              {totals.qty.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })}
            </div>
            <p className="text-[11px] text-[#94A3B8] mt-0.5">
              napříč {filteredItems.length} položkami
              {locationFilter !== 'all' && <> · <strong className="text-[#1C1917]">{locationFilter}</strong></>}
              {debouncedSearch && ' · hledání'}
            </p>
          </div>
          <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-[#FFF7ED] to-white shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase font-bold text-amber-700/80">
              <Package className="w-3.5 h-3.5" /> Součet účetní hodnoty
            </div>
            <div className="text-2xl font-bold text-[#052E16] mt-1 tabular-nums" data-testid="warehouse-total-value">
              {totals.value.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} Kč
            </div>
            <p className="text-[11px] text-[#94A3B8] mt-0.5">
              napříč {filteredItems.length} položkami
              {locationFilter !== 'all' && <> · <strong className="text-[#1C1917]">{locationFilter}</strong></>}
              {debouncedSearch && ' · hledání'}
            </p>
          </div>
        </div>
      )}

      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Package className="w-5 h-5 text-[#16A34A]" />
              Sklad NES
            </CardTitle>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Hledat dle zkratky, zboží..."
                  data-testid="warehouse-search-input"
                  className="pl-10 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
                />
              </div>
              <Button
                onClick={handleExport}
                disabled={sortedItems.length === 0}
                data-testid="warehouse-export-btn"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white shrink-0"
                title="Exportovat zobrazená data do Excelu"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Excel</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#57534E]">Načítám data...</div>
          ) : sortedItems.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    {columns.map(col => (
                      <TableHead key={col.key} className="text-[#57534E]">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSort(col.key)}
                          data-testid={`sort-${col.key}`}
                          className="text-[#57534E] hover:text-[#16A34A] font-medium -ml-2 h-auto py-1"
                        >
                          {col.label}
                          <ArrowUpDown className={`w-3 h-3 ml-1 ${sortField === col.key ? 'text-[#16A34A]' : 'text-[#CBD5E1]'}`} />
                        </Button>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedItems.map((item) => (
                    <TableRow key={item.id} className="border-[#E2E8F0] hover:bg-[#F0FDF4]" data-testid={`warehouse-row-${item.id}`}>
                      <TableCell className="text-[#1C1917] font-mono text-sm">{item.zkratka}</TableCell>
                      <TableCell className="text-[#1C1917] max-w-xs">
                        <span className="line-clamp-2">{item.zbozi}</span>
                      </TableCell>
                      <TableCell className="text-[#57534E] whitespace-nowrap">{formatDate(item.expirace)}</TableCell>
                      <TableCell>
                        {item.umisteni === 'VÝPRODEJ' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A]">
                            {item.umisteni}
                          </span>
                        ) : item.umisteni === 'SARZE' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#DBEAFE] text-[#1E40AF] border border-[#93C5FD]">
                            {item.umisteni}
                          </span>
                        ) : (
                          <span className="text-[#57534E]">{item.umisteni}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#57534E]">{item.dispozice}</TableCell>
                      <TableCell className="text-[#1C1917] text-right font-mono text-sm">{formatCurrency(item.ucetni_hodnota)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-12 text-center text-[#57534E]">
              <Package className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
              <p className="font-medium">Žádná data ve skladu</p>
              <p className="text-sm mt-1">Data budou dostupná po importu Excelu administrátorem.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {sortedItems.length > 0 && (
        <p className="text-xs text-[#94A3B8] text-right" data-testid="warehouse-count">
          Zobrazeno {sortedItems.length} z {items.length} položek
        </p>
      )}
    </div>
  );
}

// ==================== NÁKUP TAB ====================
function PurchasesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedWeeks, setSelectedWeeks] = useState(null); // null = all
  const [sortBy, setSortBy] = useState('qty_desc'); // default: T-1 množství sestupně
  const perPage = 30;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, per_page: perPage, sort_by: sortBy };
        if (debouncedSearch) params.search = debouncedSearch;
        const { data: resp } = await axios.get(`${API}/purchases/weekly`, { params, withCredentials: true });
        setData(resp);
      } catch (error) {
        console.error('Error fetching purchases:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debouncedSearch, page, sortBy]);

  if (loading && !data) {
    return <div className="p-12 text-center text-[#57534E]">Načítám data nákupů...</div>;
  }

  if (!data || data.total === 0) {
    return (
      <div className="p-12 text-center text-[#57534E]">
        <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
        <p className="font-medium">Žádná data nákupů</p>
        <p className="text-sm mt-1">Importujte Excel v administraci (záložka Nákup).</p>
      </div>
    );
  }

  const allWeeks = (data.available_weeks || []).slice().reverse();
  const weeks = selectedWeeks ? allWeeks.filter(w => selectedWeeks.includes(w)) : allWeeks;
  const totalPages = Math.ceil(data.total / perPage);

  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const days = Math.floor((now - startOfYear) / 86400000);
  const currentWeek = Math.ceil((days + startOfYear.getDay() + 1) / 7);

  return (
    <div className="space-y-4">
      {/* Today's date and week */}
      <div className="flex items-center gap-4 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl px-5 py-3" data-testid="purchases-today-info">
        <Calendar className="w-6 h-6 text-[#16A34A] flex-shrink-0" />
        <div>
          <span className="text-2xl font-bold text-[#052E16] font-['Manrope']">
            {now.toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
          <span className="ml-4 text-xl font-bold text-[#16A34A]">
            Týden {currentWeek}
          </span>
        </div>
      </div>

      {/* Search + week filter */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat dle ID zboží nebo názvu..."
              data-testid="purchases-search-input"
              className="pl-10 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
            />
          </div>
          <p className="text-sm text-[#57534E]">
            {data.total} surovin · {selectedWeeks ? `Zobrazeno ${selectedWeeks.length} z ${allWeeks.length} týdnů` : `Týdny ${allWeeks[0]}–${allWeeks[allWeeks.length - 1]}`}
          </p>
        </div>
        {/* Sort chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-[#57534E] mr-1">Řazení:</span>
          {[
            { key: 'qty_desc', label: 'Množství T-1 ↓' },
            { key: 'qty_asc', label: 'Množství T-1 ↑' },
            { key: 'name', label: 'Název A–Z' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => { setSortBy(opt.key); setPage(1); }}
              data-testid={`sort-${opt.key}`}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                sortBy === opt.key
                  ? 'bg-[#16A34A] text-white'
                  : 'bg-[#F1F5F9] text-[#57534E] hover:bg-[#E2E8F0]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {/* Week filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-[#57534E] mr-1">Týdny:</span>
          <button
            onClick={() => setSelectedWeeks(null)}
            data-testid="week-filter-all"
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
              !selectedWeeks
                ? 'bg-[#16A34A] text-white'
                : 'bg-[#F1F5F9] text-[#57534E] hover:bg-[#E2E8F0]'
            }`}
          >
            Vše
          </button>
          {allWeeks.map(w => {
            const isActive = selectedWeeks?.includes(w);
            return (
              <button
                key={w}
                onClick={() => {
                  if (!selectedWeeks) {
                    setSelectedWeeks([w]);
                  } else if (isActive) {
                    const next = selectedWeeks.filter(x => x !== w);
                    setSelectedWeeks(next.length ? next : null);
                  } else {
                    setSelectedWeeks([...selectedWeeks, w].sort((a, b) => b - a));
                  }
                }}
                data-testid={`week-filter-${w}`}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  !selectedWeeks || isActive
                    ? !selectedWeeks ? 'bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]' : 'bg-[#16A34A] text-white'
                    : 'bg-[#F1F5F9] text-[#94A3B8] hover:bg-[#E2E8F0] hover:text-[#57534E]'
                }`}
              >
                {w}
              </button>
            );
          })}
        </div>
      </div>

      {/* Items table */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                  <TableHead className="text-[#57534E] sticky left-0 bg-white z-10 min-w-[80px]">ID</TableHead>
                  <TableHead className="text-[#57534E] sticky left-[80px] bg-white z-10 min-w-[200px]">Surovina</TableHead>
                  <TableHead className="text-[#57534E] text-center min-w-[50px]">MJ</TableHead>
                  {weeks.map(w => (
                    <TableHead key={w} className="text-center min-w-[140px]" colSpan={1}>
                      <div className="text-[#052E16] font-semibold text-xs">Týden {w}</div>
                      <div className="flex text-[10px] text-[#94A3B8] gap-0 mt-0.5">
                        <span className="flex-1">Mn.</span>
                        <span className="flex-1">Kč/MJ</span>
                        <span className="flex-1">Δ</span>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((item) => {
                  const weekMap = {};
                  item.weeks.forEach(w => { weekMap[w.week] = w; });

                  return (
                    <TableRow key={item.zbozi_id} className="border-[#E2E8F0] hover:bg-[#F0FDF4]" data-testid={`purchase-row-${item.zbozi_id}`}>
                      <TableCell className="font-mono text-xs text-[#57534E] sticky left-0 bg-white z-10">{item.zbozi_id}</TableCell>
                      <TableCell className="sticky left-[80px] bg-white z-10">
                        <span className="text-sm text-[#1C1917] line-clamp-1" title={item.nazev}>{item.nazev}</span>
                      </TableCell>
                      <TableCell className="text-center text-xs text-[#94A3B8]">{item.mj}</TableCell>
                      {weeks.map(w => {
                        const wd = weekMap[w];
                        if (!wd) return (
                          <TableCell key={w} className="text-center text-[#CBD5E1] text-xs">—</TableCell>
                        );
                        return (
                          <TableCell key={w} className="text-center p-1">
                            <div className="flex items-center text-xs gap-0">
                              <span className="flex-1 text-[#1C1917]">{Math.round(wd.qty)}</span>
                              <span className="flex-1 font-mono text-[#57534E]">{wd.price.toFixed(1)}</span>
                              <span className="flex-1">
                                {wd.price_diff != null && wd.price_diff !== 0 ? (
                                  <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${
                                    wd.price_diff > 0 ? 'text-red-600' : 'text-green-600'
                                  }`}>
                                    {wd.price_diff > 0 ? (
                                      <TrendingUp className="w-2.5 h-2.5" />
                                    ) : (
                                      <TrendingDown className="w-2.5 h-2.5" />
                                    )}
                                    {wd.price_diff > 0 ? '+' : ''}{wd.price_diff_pct}%
                                  </span>
                                ) : wd.price_diff === 0 ? (
                                  <span className="text-[10px] text-[#94A3B8]">=</span>
                                ) : null}
                              </span>
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#94A3B8]">
            Strana {page} z {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              data-testid="purchases-prev-page"
              className="border-[#E2E8F0]"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              data-testid="purchases-next-page"
              className="border-[#E2E8F0]"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================

// ==================== PRŮVODKY TAB ====================
function PruvodkyTab() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [kalkulace, setKalkulace] = useState([]);
  const [sortField, setSortField] = useState('produkt');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`${API}/pruvodky/products`, { withCredentials: true });
        setProducts(data);
      } catch { /* empty */ }
      finally { setLoading(false); }
    };
    fetchProducts();
  }, []);

  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    setHistoryLoading(true);
    try {
      const [histRes, kalkRes] = await Promise.all([
        axios.get(`${API}/pruvodky/history`, { params: { product }, withCredentials: true }),
        axios.get(`${API}/kalkulace/history`, { params: { product }, withCredentials: true }),
      ]);
      setHistory(histRes.data);
      setKalkulace(kalkRes.data);
    } catch { setHistory([]); setKalkulace([]); }
    finally { setHistoryLoading(false); }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'produkt' ? 'asc' : 'desc');
    }
  };

  const filtered = search
    ? products.filter(p => p.produkt.toLowerCase().includes(search.toLowerCase()) || (p.vyrobek && p.vyrobek.toLowerCase().includes(search.toLowerCase())))
    : products;

  const sorted = [...filtered].sort((a, b) => {
    let valA, valB;
    if (sortField === 'produkt') {
      valA = a.produkt.toLowerCase();
      valB = b.produkt.toLowerCase();
      return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
    if (sortField === 'diff') {
      valA = a.avg_skut_ks - a.cena_kalk_ks;
      valB = b.avg_skut_ks - b.cena_kalk_ks;
    } else if (sortField === 'avg_plneni') {
      valA = a.avg_plneni || 0;
      valB = b.avg_plneni || 0;
    } else {
      valA = a[sortField] || 0;
      valB = b[sortField] || 0;
    }
    return sortDir === 'asc' ? valA - valB : valB - valA;
  });

  const SortHeader = ({ field, children, align }) => (
    <TableHead
      className={`text-[#57534E] cursor-pointer hover:text-[#16A34A] select-none ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field && (
          <ArrowUpDown className="w-3 h-3 text-[#16A34A]" />
        )}
      </span>
    </TableHead>
  );

  if (loading) return <div className="p-12 text-center text-[#57534E]">Načítám data průvodek...</div>;

  if (products.length === 0) {
    return (
      <div className="p-12 text-center text-[#57534E]">
        <Package className="w-12 h-12 mx-auto mb-3 text-[#CBD5E1]" />
        <p className="font-medium">Žádná data průvodek</p>
        <p className="text-sm mt-1">Importujte Excel v administraci (záložka Průvodky).</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative w-full sm:w-96">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Hledat produkt nebo ID výrobku..."
          data-testid="pruvodky-search"
          className="pl-10 bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
        />
      </div>

      {/* Product list */}
      {!selectedProduct && (
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                    <SortHeader field="produkt">Produkt</SortHeader>
                    <SortHeader field="count" align="right">Výrob</SortHeader>
                    <SortHeader field="avg_plneni" align="right">Plnění %</SortHeader>
                    <SortHeader field="avg_skut_ks" align="right">Prům. výrobní Kč/ks</SortHeader>
                    <SortHeader field="cena_kalk_ks" align="right">Kalk. Kč/ks</SortHeader>
                    <SortHeader field="diff" align="right">Δ Výroba vs Kalk.</SortHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((item) => {
                    const diff = item.avg_skut_ks - item.cena_kalk_ks;
                    return (
                      <TableRow
                        key={item.produkt}
                        className="border-[#E2E8F0] hover:bg-[#F0FDF4] cursor-pointer"
                        onClick={() => handleSelectProduct(item.produkt)}
                      >
                        <TableCell className="text-sm text-[#1C1917] max-w-[300px]">
                          <span className="line-clamp-1">{item.produkt}</span>
                          {item.vyrobek && <span className="block text-[10px] text-[#94A3B8] font-mono">{item.vyrobek}</span>}
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-[#57534E]">{item.count}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          <span className={`font-semibold ${item.avg_plneni >= 100 ? 'text-[#16A34A]' : item.avg_plneni >= 95 ? 'text-[#57534E]' : 'text-red-600'}`}>
                            {item.avg_plneni}%
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums font-medium">{item.avg_skut_ks.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums text-[#16A34A] font-medium">{item.cena_kalk_ks.toFixed(2)}</TableCell>
                        <TableCell className="text-right text-sm tabular-nums font-bold">
                          <span className={diff > 0 ? 'text-red-600' : diff < 0 ? 'text-[#16A34A]' : 'text-[#57534E]'}>
                            {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-[#94A3B8]">Žádný produkt neodpovídá</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Product history */}
      {selectedProduct && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedProduct(null); setHistory([]); }}
              className="text-[#57534E] hover:text-[#16A34A]"
              data-testid="pruvodky-back"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Zpět
            </Button>
            <h3 className="text-base font-bold text-[#052E16] font-['Manrope'] line-clamp-1">{selectedProduct}</h3>
          </div>

          {historyLoading ? (
            <div className="p-8 text-center text-[#57534E]">Načítám historii...</div>
          ) : history.length > 0 ? (
            <Card className="bg-white border-[#E2E8F0] shadow-sm">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                        <TableHead className="text-[#57534E]">Datum výroby</TableHead>
                        <TableHead className="text-[#57534E]">Datum kalk.</TableHead>
                        <TableHead className="text-[#57534E] text-right">Plán. ks</TableHead>
                        <TableHead className="text-[#57534E] text-right">Skut. ks</TableHead>
                        <TableHead className="text-[#57534E] text-right">Plnění</TableHead>
                        <TableHead className="text-[#57534E] text-right">Plán. Kč/ks</TableHead>
                        <TableHead className="text-[#57534E] text-right">Skut. Kč/ks</TableHead>
                        <TableHead className="text-[#57534E] text-right">Kalk. Kč/ks</TableHead>
                        <TableHead className="text-[#57534E] text-right">Δ Skut. vs Kalk.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map((row, idx) => {
                        const diff = row.cena_skut_ks - row.cena_kalk_ks;
                        const diffPct = row.cena_kalk_ks > 0 ? ((diff / row.cena_kalk_ks) * 100) : 0;
                        return (
                          <TableRow key={idx} className="border-[#E2E8F0] hover:bg-[#F8FAF8]">
                            <TableCell className="text-xs text-[#1C1917] font-medium whitespace-nowrap">{row.datum_potvrzeni || '—'}</TableCell>
                            <TableCell className="text-xs text-[#57534E] whitespace-nowrap">{row.datum_kalkulace || '—'}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums">{row.mn_plan.toLocaleString('cs-CZ')}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums font-medium">{row.mn_skut.toLocaleString('cs-CZ')}</TableCell>
                            <TableCell className="text-right text-xs">
                              <span className={`font-semibold ${row.plneni_pct >= 100 ? 'text-[#16A34A]' : row.plneni_pct >= 95 ? 'text-[#57534E]' : 'text-red-600'}`}>
                                {row.plneni_pct}%
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm tabular-nums text-[#57534E]">{row.cena_plan_ks.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums font-medium">{row.cena_skut_ks.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-sm tabular-nums text-[#16A34A] font-medium">{row.cena_kalk_ks.toFixed(2)}</TableCell>
                            <TableCell className="text-right text-xs font-bold">
                              <span className={diff > 0 ? 'text-red-600' : diff < 0 ? 'text-[#16A34A]' : 'text-[#57534E]'}>
                                {diff > 0 ? '+' : ''}{diff.toFixed(2)} ({diffPct > 0 ? '+' : ''}{diffPct.toFixed(1)}%)
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="p-8 text-center text-[#94A3B8]">Žádná historie pro tento produkt</div>
          )}

          {history.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="bg-white border-[#E2E8F0] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-[#57534E] uppercase tracking-wider mb-1">Průměrná skut. cena/ks</p>
                  <p className="text-2xl font-bold text-[#052E16]">
                    {(history.reduce((s, r) => s + r.cena_skut_ks, 0) / history.length).toFixed(2)} Kč
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#E2E8F0] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-[#57534E] uppercase tracking-wider mb-1">Kalkulovaná cena/ks</p>
                  <p className="text-2xl font-bold text-[#16A34A]">
                    {history[0]?.cena_kalk_ks.toFixed(2)} Kč
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#E2E8F0] shadow-sm">
                <CardContent className="pt-5 pb-4">
                  <p className="text-xs text-[#57534E] uppercase tracking-wider mb-1">Počet výrob</p>
                  <p className="text-2xl font-bold text-[#052E16]">{history.length}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Kalkulace history */}
          {kalkulace.length > 0 && (
            <Card className="bg-white border-[#E2E8F0] shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-[#052E16] font-['Manrope'] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#16A34A]" />
                  Historie kalkulací
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                        <TableHead className="text-[#57534E]">Vytvořeno</TableHead>
                        <TableHead className="text-[#57534E]">Změněno</TableHead>
                        <TableHead className="text-[#57534E] text-right">Cena Kč/ks</TableHead>
                        <TableHead className="text-[#57534E]">Doklad</TableHead>
                        <TableHead className="text-[#57534E]">Vytvořil</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kalkulace.map((row, idx) => (
                        <TableRow key={idx} className="border-[#E2E8F0] hover:bg-[#F8FAF8]">
                          <TableCell className="text-xs text-[#57534E] whitespace-nowrap">{row.vytvoreno || '—'}</TableCell>
                          <TableCell className="text-xs text-[#1C1917] font-medium whitespace-nowrap">{row.zmeneno || '—'}</TableCell>
                          <TableCell className="text-right text-sm tabular-nums font-bold text-[#16A34A]">{row.cena.toFixed(4)}</TableCell>
                          <TableCell className="text-xs text-[#57534E] font-mono">{row.doklad}</TableCell>
                          <TableCell className="text-xs text-[#57534E]">{row.vytvoril}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== NUTRIČNÍ PÉČE (Medevio) TAB ====================
function NutricniPeceTab() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [editPrices, setEditPrices] = useState(false);
  const [priceDraft, setPriceDraft] = useState({});
  const [savingPrices, setSavingPrices] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(`${API}/medevio/dashboard`, { withCredentials: true });
      setData(d);
      setPriceDraft(d.prices || {});
    } catch (e) {
      setError(e.response?.data?.detail || 'Nepodařilo se načíst data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/medevio/refresh`, {}, { withCredentials: true });
      await load();
    } catch (e) {
      // ignore
    } finally {
      setRefreshing(false);
    }
  };

  const handleSavePrices = async () => {
    setSavingPrices(true);
    try {
      await axios.post(`${API}/medevio/prices`, { prices: priceDraft }, { withCredentials: true });
      await handleRefresh();
      setEditPrices(false);
    } catch (e) {
      // ignore
    } finally {
      setSavingPrices(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-[#57534E]">Načítám data z Medevio…</div>;
  if (error) return (
    <Card className="bg-red-50 border-red-200">
      <CardContent className="py-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 font-semibold">{error}</p>
        <Button onClick={load} variant="outline" className="mt-4">Zkusit znovu</Button>
      </CardContent>
    </Card>
  );
  if (!data) return null;

  const s = data.summary;
  const fmtMoney = (v) => (v || 0).toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' Kč';
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('cs-CZ');
  };
  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Prague' });
  };
  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString('cs-CZ', { timeStyle: 'short', timeZone: 'Europe/Prague' });
  };
  const monthLabel = (key) => {
    const [y, m] = key.split('-');
    const months = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čer', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'];
    return `${months[parseInt(m, 10) - 1]} ${y}`;
  };
  const maxTypeCount = Math.max(1, ...data.types.map(t => t.count));
  const maxMonthCount = Math.max(1, ...data.months.map(m => m.count));

  const statusLabel = { done: 'Hotovo', overdue: 'Po termínu', waiting: 'Čeká' };
  const statusColor = {
    done: 'bg-green-100 text-green-800 border-green-200',
    overdue: 'bg-red-100 text-red-800 border-red-200',
    waiting: 'bg-amber-100 text-amber-800 border-amber-200',
  };

  return (
    <div className="space-y-6" data-testid="nutricni-pece-tab">
      {/* Header with refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-[#16A34A]" /> Nutriční péče – Medevio
          </h2>
          <p className="text-xs text-[#94A3B8] mt-0.5">
            Klinika: <span className="font-mono">{data.clinic_slug}</span>
            {data.data_from && <> · zobrazeno od <strong className="text-[#1C1917]">{fmtDate(data.data_from)}</strong></>}
            {' · aktualizováno '}{fmtDateTime(data.fetched_at)}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              onClick={() => setEditPrices(v => !v)}
              variant="outline"
              data-testid="toggle-prices-btn"
              className="border-[#E2E8F0] text-[#57534E]"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              {editPrices ? 'Skrýt ceny' : 'Nastavit ceny'}
            </Button>
          )}
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="medevio-refresh-btn"
            className="bg-[#16A34A] hover:bg-[#15803D] text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Aktualizuji…' : 'Obnovit'}
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="medevio-summary-tiles">
        <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-[#94A3B8]"><Layers className="w-3.5 h-3.5" /> Požadavků celkem</div>
          <div className="text-3xl font-bold text-[#052E16] mt-1 tabular-nums">{s.total}</div>
          <p className="text-[11px] text-[#94A3B8] mt-0.5">napříč {data.types.length} službami</p>
        </div>
        <div className="p-4 rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 to-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Vyřešeno</div>
          <div className="text-3xl font-bold text-[#052E16] mt-1 tabular-nums">{s.done}</div>
          <p className="text-[11px] text-green-700/70 mt-0.5">{s.total > 0 ? Math.round(s.done / s.total * 100) : 0}% z celku</p>
        </div>
        <div className="p-4 rounded-2xl border border-red-200 bg-gradient-to-br from-red-50 to-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-red-700"><AlertTriangle className="w-3.5 h-3.5" /> Po termínu</div>
          <div className="text-3xl font-bold text-[#052E16] mt-1 tabular-nums">{s.overdue}</div>
          <p className="text-[11px] text-red-700/70 mt-0.5">neuzavřené po dueDate</p>
        </div>
        <div className="p-4 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-amber-700"><Clock className="w-3.5 h-3.5" /> Čeká na vyřízení</div>
          <div className="text-3xl font-bold text-[#052E16] mt-1 tabular-nums">{s.waiting}</div>
          <p className="text-[11px] text-amber-700/70 mt-0.5">v termínu, ale ještě otevřeno</p>
        </div>
      </div>

      {/* Reservations + Revenue tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-[#94A3B8]"><CalendarClock className="w-3.5 h-3.5" /> Rezervací celkem</div>
          <div className="text-2xl font-bold text-[#052E16] mt-1 tabular-nums">{s.total_reservations}</div>
        </div>
        <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-[#94A3B8]"><XCircle className="w-3.5 h-3.5" /> Stornováno</div>
          <div className="text-2xl font-bold text-[#052E16] mt-1 tabular-nums">{s.canceled_reservations}</div>
          <p className="text-[11px] text-[#94A3B8]">
            {s.total_reservations > 0 ? Math.round(s.canceled_reservations / s.total_reservations * 100) : 0}%
          </p>
        </div>
        <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-[#94A3B8]"><Calendar className="w-3.5 h-3.5" /> Nadcházející</div>
          <div className="text-2xl font-bold text-[#052E16] mt-1 tabular-nums">{s.upcoming_reservations}</div>
        </div>
        <div className="p-4 rounded-2xl border border-[#E2E8F0] bg-gradient-to-br from-[#FFF7ED] to-white shadow-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase font-bold text-amber-700"><DollarSign className="w-3.5 h-3.5" /> Tržby (vyřešené)</div>
          <div className="text-2xl font-bold text-[#052E16] mt-1 tabular-nums">{fmtMoney(s.revenue)}</div>
          {s.revenue === 0 && <p className="text-[10px] text-amber-700/70">Nastavte ceny služeb</p>}
        </div>
      </div>

      {/* Service types chart */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#16A34A]" /> Služby podle počtu požadavků
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.types.map(t => {
            const pct = (t.count / maxTypeCount) * 100;
            return (
              <div key={t.id || t.name} className="space-y-1" data-testid={`type-row-${t.id}`}>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-medium text-[#1C1917] truncate">{t.name}</span>
                  <span className="text-xs text-[#57534E] shrink-0 tabular-nums">
                    {t.count}× · {fmtMoney(t.revenue)} · {t.duration_minutes} min
                  </span>
                </div>
                <div className="h-2.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${t.requires_payment ? 'bg-gradient-to-r from-[#16A34A] to-[#22C55E]' : 'bg-gradient-to-r from-slate-400 to-slate-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Two-column: Months + Top patients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#16A34A]" /> Vývoj po měsících
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.months.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-4">Žádná data</p>
            ) : (
              <div className="flex items-end gap-2 h-40">
                {data.months.map(m => {
                  const h = (m.count / maxMonthCount) * 100;
                  return (
                    <div key={m.month} className="flex-1 flex flex-col items-center justify-end group" title={`${m.count} požadavků, ${fmtMoney(m.revenue)}`}>
                      <span className="text-[10px] text-[#57534E] mb-1 tabular-nums opacity-0 group-hover:opacity-100 transition-opacity">{m.count}</span>
                      <div
                        className="w-full bg-gradient-to-t from-[#16A34A] to-[#22C55E] rounded-t-md min-h-[4px]"
                        style={{ height: `${h}%` }}
                      />
                      <span className="text-[10px] text-[#94A3B8] mt-1 whitespace-nowrap">{monthLabel(m.month)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#16A34A]" /> Top pacienti
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.top_patients.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-4">Žádná data</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="text-left py-2 px-4 font-semibold">Pacient</th>
                    <th className="text-right py-2 px-4 font-semibold">Požadavků</th>
                    <th className="text-right py-2 px-4 font-semibold">Útrata</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_patients.map(p => (
                    <tr key={p.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAF8]">
                      <td className="py-2 px-4 text-[#1C1917]">{p.name || '(bez jména)'}</td>
                      <td className="py-2 px-4 text-right tabular-nums font-semibold">{p.count}</td>
                      <td className="py-2 px-4 text-right tabular-nums text-[#57534E]">{fmtMoney(p.spent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reservations list */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#16A34A]" /> Rezervace
            <span className="text-xs font-medium text-[#94A3B8]">
              ({(data.reservations || []).length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]">
                <tr>
                  <th className="text-left py-2 px-4 font-semibold">Datum a čas</th>
                  <th className="text-left py-2 px-4 font-semibold">Pacient</th>
                  <th className="text-left py-2 px-4 font-semibold">Služba</th>
                  <th className="text-left py-2 px-4 font-semibold">Stav</th>
                  <th className="text-left py-2 px-4 font-semibold">Důvod storna</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(data.reservations || []).map(r => {
                  const stColor = {
                    upcoming: 'bg-blue-100 text-blue-800 border-blue-200',
                    past: 'bg-slate-100 text-slate-700 border-slate-200',
                    canceled: 'bg-red-100 text-red-800 border-red-200',
                    unknown: 'bg-amber-100 text-amber-800 border-amber-200',
                  };
                  const stLabel = {
                    upcoming: 'Nadcházející',
                    past: 'Proběhlo',
                    canceled: 'Storno',
                    unknown: '—',
                  };
                  return (
                    <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAF8]" data-testid={`reservation-${r.id}`}>
                      <td className="py-2 px-4 text-[#1C1917] whitespace-nowrap">
                        <span className="font-semibold">{fmtDateTime(r.start)}</span>
                        {r.end && <span className="text-xs text-[#94A3B8] ml-1">— {fmtTime(r.end)}</span>}
                      </td>
                      <td className="py-2 px-4 text-[#1C1917]">{r.patient_name || '—'}</td>
                      <td className="py-2 px-4 text-[#57534E]">{r.type_name}</td>
                      <td className="py-2 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${stColor[r.status] || stColor.unknown}`}>
                          {stLabel[r.status] || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-xs text-[#94A3B8]">
                        {r.canceled_reason_text || r.canceled_reason || '—'}
                      </td>
                      <td className="py-2 px-2">
                        {r.request_url && (
                          <a href={r.request_url} target="_blank" rel="noopener noreferrer" className="text-[#16A34A] hover:text-[#15803D]" title="Otevřít v Medevio">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(data.reservations || []).length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-sm text-[#94A3B8]">Žádné rezervace</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent requests */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope']">
            Poslední požadavky
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-[#94A3B8] border-b border-[#E2E8F0]">
              <tr>
                <th className="text-left py-2 px-4 font-semibold">Pacient</th>
                <th className="text-left py-2 px-4 font-semibold">Služba</th>
                <th className="text-left py-2 px-4 font-semibold">Vytvořeno</th>
                <th className="text-left py-2 px-4 font-semibold">Termín</th>
                <th className="text-left py-2 px-4 font-semibold">Stav</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map(r => (
                <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAF8]">
                  <td className="py-2 px-4 text-[#1C1917]">{r.patient_name || '—'}</td>
                  <td className="py-2 px-4 text-[#57534E]">{r.type_name}</td>
                  <td className="py-2 px-4 text-[#57534E] text-xs whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="py-2 px-4 text-[#57534E] text-xs whitespace-nowrap">{fmtDate(r.due_date)}</td>
                  <td className="py-2 px-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusColor[r.status]}`}>
                      {statusLabel[r.status]}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[#16A34A] hover:text-[#15803D]" title="Otevřít v Medevio">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Price editor (admin) */}
      {isAdmin && editPrices && (
        <Card className="bg-amber-50/30 border-amber-200 shadow-sm" data-testid="medevio-prices-editor">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold text-[#052E16] font-['Manrope'] flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-700" /> Ceny služeb (pro výpočet tržeb)
            </CardTitle>
            <p className="text-xs text-[#57534E]">
              Medevio API neukládá ceny – zadejte cenu za jednu vyřešenou službu. Tržby se počítají z vyřešených (doneAt) požadavků.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.all_types || []).filter(t => t.requires_payment).map(t => (
                <div key={t.id} className="flex items-center gap-3 p-2 rounded-lg bg-white border border-[#E2E8F0]">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1C1917]">{t.name}</p>
                    <p className="text-[11px] text-[#94A3B8]">{t.duration} min · vyžaduje platbu</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Input
                      type="number"
                      value={priceDraft[t.id] || ''}
                      onChange={(e) => setPriceDraft(prev => ({ ...prev, [t.id]: e.target.value }))}
                      placeholder="0"
                      className="w-28 h-9 text-right"
                      data-testid={`price-input-${t.id}`}
                    />
                    <span className="text-sm text-[#57534E]">Kč</span>
                  </div>
                </div>
              ))}
              <Button
                onClick={handleSavePrices}
                disabled={savingPrices}
                data-testid="save-prices-btn"
                className="bg-[#16A34A] hover:bg-[#15803D] text-white mt-3"
              >
                <Save className="w-4 h-4 mr-2" /> {savingPrices ? 'Ukládám…' : 'Uložit ceny a přepočítat'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ==================== DATA VALIDITY BADGE ====================
function DataValidityBadge({ meta, label, extraMeta = null, extraLabel = null }) {
  const fmt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
  };
  const item = (m, lab) => {
    if (!m || !m.date) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-[#94A3B8]">
          <Clock className="w-3 h-3" /> {lab}: nenahráno
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs text-[#57534E]" title={m.filename || ''}>
        <Clock className="w-3 h-3 text-[#16A34A]" />
        <span>Data <strong className="text-[#1C1917]">{lab}</strong> platná k <strong className="text-[#1C1917]">{fmt(m.date)}</strong></span>
      </span>
    );
  };
  return (
    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mb-3" data-testid={`data-validity-${label}`}>
      {item(meta, label)}
      {extraLabel && item(extraMeta, extraLabel)}
    </div>
  );
}

export default function OverviewsPage() {
  const { canViewOverviews } = useAuth();
  const [lastImports, setLastImports] = useState({});

  useEffect(() => {
    if (canViewOverviews) {
      axios.get(`${API}/overviews/last-imports`, { withCredentials: true })
        .then(r => setLastImports(r.data || {}))
        .catch(() => {});
    }
  }, [canViewOverviews]);

  if (!canViewOverviews) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="overviews-no-access">
          <Card className="bg-white border-[#E2E8F0] shadow-sm max-w-md w-full">
            <CardContent className="py-16 text-center">
              <Lock className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#052E16] font-['Manrope'] mb-2">Přístup odepřen</h2>
              <p className="text-[#57534E]">
                Nemáte oprávnění k zobrazení této stránky. Kontaktujte administrátora pro přidělení přístupu.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const now = new Date();
  const onejan = new Date(now.getFullYear(), 0, 1);
  const dayOfYear = Math.floor((now - onejan) / 86400000);
  const dayOfWeek = onejan.getDay() || 7; // Monday=1
  const currentWeek = Math.ceil((dayOfYear + dayOfWeek) / 7);

  // Monday of current week
  const today = new Date();
  const mondayOffset = today.getDay() === 0 ? -6 : 1 - today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmtDate = (d) => d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });

  return (
    <Layout>
      <div className="space-y-6" data-testid="overviews-page">
        <div className="flex items-end gap-4">
          <div>
            <Link to="/analytika" className="inline-flex items-center gap-1 text-sm text-[#57534E] hover:text-[#16A34A] mb-1" data-testid="overviews-back-link">
              ← Analytika
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#052E16] font-['Manrope']">
              Přehledy (staré)
            </h1>
            <p className="text-[#57534E] mt-2">
              Skladové přehledy a data
          </p>
          </div>
          <div className="flex flex-col items-center px-5 py-2.5 rounded-2xl bg-[#F0FDF4] border border-[#BBF7D0]" data-testid="current-week-badge">
            <span className="text-[10px] font-medium text-[#57534E] uppercase tracking-wider">Aktuální týden</span>
            <span className="text-2xl font-black text-[#16A34A] tabular-nums leading-tight">{currentWeek}</span>
            <span className="text-[10px] text-[#94A3B8]">{fmtDate(monday)} – {fmtDate(sunday)}</span>
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList className="bg-[#F8FAF8] border border-[#E2E8F0]">
            <TabsTrigger
              value="dashboard"
              data-testid="tab-overview-dashboard"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Dashboard
            </TabsTrigger>
            <TabsTrigger
              value="sklad"
              data-testid="tab-overview-sklad"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Sklad NES
            </TabsTrigger>
            <TabsTrigger
              value="nakup"
              data-testid="tab-overview-nakup"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Nákup
            </TabsTrigger>
            <TabsTrigger
              value="pruvodky"
              data-testid="tab-overview-pruvodky"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Průvodky
            </TabsTrigger>
            <TabsTrigger
              value="nutricni"
              data-testid="tab-overview-nutricni"
              className="data-[state=active]:bg-[#F0FDF4] data-[state=active]:text-[#16A34A]"
            >
              Nutriční péče
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DataValidityBadge meta={lastImports.l1dashboard} label="L1 Dashboard" />
            <OverviewDashboard />
          </TabsContent>

          <TabsContent value="sklad">
            <DataValidityBadge meta={lastImports.warehouse} label="Sklad NES" />
            <SkladNesTable />
          </TabsContent>

          <TabsContent value="nakup">
            <DataValidityBadge meta={lastImports.purchases} label="Nákup" />
            <PurchasesTab />
          </TabsContent>

          <TabsContent value="pruvodky">
            <DataValidityBadge meta={lastImports.pruvodky} label="Průvodky" extraMeta={lastImports.kalkulace} extraLabel="Kalkulace" />
            <PruvodkyTab />
          </TabsContent>

          <TabsContent value="nutricni">
            <NutricniPeceTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
